import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import dayjs, { Dayjs } from 'dayjs';

import { errorNormalizer } from '../../../../shared/Helpers/functions';
import { CustomButton, Picker, Text } from '../../../../shared/uiComponents';
import { EditSvg } from '../../../../shared/Assets/svgIcons';
import { Time } from '../../../../shared/Assets/sessionDetailsSVGs';
import { TEXT } from '../../../../shared/Helpers/colors';
import { AdminTypes } from '../../../../components/Menu';
import { useResetCalendarData } from '../../Helpers/hooks';
import { isPastTime } from '../../Helpers/functions';
import {
	DurationIncreaseMBHWarningDialog,
	DurationIncreaseWarningDialog,
	MBHBillingWarningDialog,
} from '../../../SOAPNote/components/postSubmitDialogs';

import { DispatchProperties, useSelector } from '../../../../redux/store';
import { UpdateSessionProperties } from '../../../../redux/API/ClientAPIHelpers/sessionsProperties';
import { SoapNotesStatuses } from '../../../../redux/API/ClientAPIHelpers/dataCollectionProperties';
import { MBHBillingStatus } from '../../../../redux/API/ClientAPIHelpers/billingProperties';
import { updateSession } from '../../../../redux/State/clientSlice/sessionSlice';

const TimeInfo = () => {
	const dispatch = useDispatch<DispatchProperties>();
	const { resetData } = useResetCalendarData();
	const [originalDurationMinutes, setOriginalDurationMinutes] = useState<number>(60);
	const [edit, setEdit] = useState<boolean>(false);

	const [billingWarningOpen, setBillingWarningOpen] = useState<boolean>(false);
	const [durationWarningOpen, setDurationWarningOpen] = useState<boolean>(false);
	const [durationMBHWarningOpen, setDurationMBHWarningOpen] = useState<boolean>(false);
	const [saving, setSaving] = useState<boolean>(false);

	const data = useSelector((state) => state.calendarHelper.infoEventData);

	const [startTimeValue, setStartTimeValue] = useState<Dayjs | null>(dayjs(data?.startTime));
	const [endTimeValue, setEndTimeValue] = useState<Dayjs | null>(dayjs(data?.endTime));
	const [isAllowed, setIsAllowed] = useState<boolean>(false);

	const role = useSelector((state) => state.account.role);
	const roleRestriction = useSelector((state) => state.account.roleRestriction);

	const isAdmin = role?.section.id === AdminTypes.admin || role?.section.id === AdminTypes.cs;

	const isSubmitted = data?.reportStatus?.status === SoapNotesStatuses.submitted;
	const mbhBillingStatus = data?.mbhBillingStatus ?? null;
	const requiresBillingConfirmation =
		mbhBillingStatus === MBHBillingStatus.Approved || mbhBillingStatus === MBHBillingStatus.Billed;

	const isDurationIncreased = (): boolean => {
		if (!startTimeValue || !endTimeValue || !data?.startTime || !data?.endTime) return false;
		const newDurationMinutes = endTimeValue.diff(startTimeValue, 'minute');

		return newDurationMinutes > originalDurationMinutes;
	};

	const openEditDateHandler = () => setEdit(true);
	const closeEditDateHandler = () => setEdit(false);

	const performSave = () => {
		if (!data?.date || !startTimeValue || !endTimeValue) return;
		const sessionId = data.sessionId;
		const newDate = data.date.split('T')[0];
		const startTime = `${newDate}T${startTimeValue.format('HH:mm')}`;
		const endTime = `${newDate}T${endTimeValue.format('HH:mm')}`;

		const updatedSession: UpdateSessionProperties = {
			id: sessionId,
			date: newDate,
			endTime,
			startTime,
		};

		setSaving(true);
		dispatch(updateSession({ sessionId, data: updatedSession }))
			.then(unwrapResult)
			.then(() => {
				resetData();
				toast('Success');
			})
			.catch(errorNormalizer)
			.finally(() => setSaving(false));
	};

	const saveDateHandler = () => {
		if (!isSubmitted) {
			performSave();

			return;
		}

		const durationIncreased = isDurationIncreased();

		if (durationIncreased) {
			if (requiresBillingConfirmation) {
				setDurationMBHWarningOpen(true);
			} else {
				setDurationWarningOpen(true);
			}
		} else if (requiresBillingConfirmation) {
			setBillingWarningOpen(true);
		} else {
			performSave();
		}
	};

	const handleBillingWarningConfirm = () => {
		setBillingWarningOpen(false);
		performSave();
	};

	const handleDurationIncreaseConfirm = () => {
		setDurationWarningOpen(false);
		setDurationMBHWarningOpen(false);
		performSave();
	};

	useEffect(() => {
		if (data?.startTime && data?.endTime) {
			const start = dayjs(data.startTime);
			const end = dayjs(data.endTime);
			const duration = end.diff(start, 'minute');
			setOriginalDurationMinutes(duration);
		}
	}, [data?.startTime, data?.endTime]);

	useEffect(() => {
		if (startTimeValue && originalDurationMinutes) {
			setEndTimeValue(startTimeValue.add(originalDurationMinutes, 'minute'));
		}
	}, [startTimeValue, originalDurationMinutes]);

	useEffect(() => {
		if (!roleRestriction) {
			setIsAllowed(true);

			return;
		}
		if (!data) return;
		const today = new Date();
		const allowedAfterDate = new Date(data.date);

		allowedAfterDate.setDate(allowedAfterDate.getDate() + roleRestriction);
		const isPast = isPastTime(today, allowedAfterDate);
		setIsAllowed(!isPast && isAdmin);
	}, [data, isAdmin, roleRestriction]);

	useEffect(() => {
		if (!data) return;
		const startTime = dayjs(data.startTime).utc(false);
		const endTime = dayjs(data.endTime).utc(false);
		setStartTimeValue(startTime);
		setEndTimeValue(endTime);
	}, [data]);

	return (
		<>
			<div className="row">
				{edit ? (
					<div className="rowContent">
						<div className="action">
							<Picker.CustomTime label="Start time" value={startTimeValue} onChange={setStartTimeValue} />
							<Picker.CustomTime label="End time" value={endTimeValue} onChange={setEndTimeValue} />
						</div>
						<div className="action">
							<CustomButton title="Save" onClick={saveDateHandler} loading={saving} />
							<CustomButton title="Cancel" buttonType="secondaryGray" onClick={closeEditDateHandler} />
						</div>
					</div>
				) : (
					<>
						<div className="icon">
							<Time />
						</div>
						<div className="rowContent">
							<div className="text">
								<Text title="Time" type="pxs" fontWeight="medium" color={TEXT.TERTIARY} />
								<Text
									title={`${dayjs(data?.startTime).utc(false).format('hh:mm A')} - 
                            ${dayjs(data?.endTime).utc(false).format('hh:mm A')}`}
									type="psm"
									fontWeight="semiBold"
								/>
							</div>
							{isAllowed && (
								<div className="actionIcon" onClick={openEditDateHandler}>
									<EditSvg />
								</div>
							)}
						</div>
					</>
				)}
			</div>
			<MBHBillingWarningDialog
				open={billingWarningOpen}
				onClose={() => setBillingWarningOpen(false)}
				onConfirm={handleBillingWarningConfirm}
				loading={saving}
			/>
			<DurationIncreaseWarningDialog
				open={durationWarningOpen}
				onClose={() => setDurationWarningOpen(false)}
				onConfirm={handleDurationIncreaseConfirm}
				loading={saving}
			/>
			<DurationIncreaseMBHWarningDialog
				open={durationMBHWarningOpen}
				onClose={() => setDurationMBHWarningOpen(false)}
				onConfirm={handleDurationIncreaseConfirm}
				loading={saving}
			/>
		</>
	);
};

export default TimeInfo;
